import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box, Typography } from "@mui/material";
import EasyAnchorLink from "../../UI/AnchorLink";

import { GridColDef } from "@mui/x-data-grid-premium";
import { renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import KuebikoQueryCell from "../UI/KuebikoQueryCell";

import "../BrandMetadataReview.css";
import PlacesDrillDownModal from "../UI/PlacesDrillDownModal";

interface PlacesOmniReviewProps {
  omni_key: string;
  country_code: string;
}

const ReviewPlacesOmniBizCategories: React.FC<PlacesOmniReviewProps> = ({
  omni_key,
  country_code,
}) => {
  const { showError } = useError();
  const { apiRequest } = useApi();

  const columns_def: GridColDef[] = [
    {
      field: "chain_id",
      headerName: "Chain ID",
      type: "string",
      width: 150,
    },
    {
      field: "external_id",
      headerName: "External ID",
      type: "string",
      width: 150,
    },
    {
      field: "omni_key",
      headerName: "Omni Key",
      type: "string",
      width: 150,
    },
    {
      field: "metadata_category",
      headerName: "Metadata Category",
      description: "The business category from the brand's metadata.",
      type: "string",
      width: 200,
    },
    {
      field: "actual_category",
      headerName: "Places Category",
      description:
        "The business category associated with the brand and country in the places table.",
      type: "string",
      width: 200,
    },
    {
      field: "business_category_count",
      headerName: "Business Category Count",
      description:
        "The count of places associated with this brand, country, and business category.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "kuebiko_query",
      headerName: "Kuebiko Query",
      description:
        "Copy or view the Kuebiko query used to review places for retraction.",
      filterable: false,
      sortable: false,
      width: 250,
      renderCell: (params) => {
        const queryTemplate = `
        SELECT id, name, country_code, business_category_id, address, chain_id, website,
            external_ids -> 'EXTERNAL_ID' AS "omni_id"
        FROM "places"
        WHERE external_ids -> 'EXTERNAL_ID' IS NOT NULL
        AND country_code = 'COUNTRY_CODE'
        AND business_category_id = 'ACTUAL_CATEGORY'
        `;

        const columnMappings = {
          EXTERNAL_ID: params.row.external_id,
          COUNTRY_CODE: params.row.country_code,
          ACTUAL_CATEGORY: params.row.actual_category,
        };

        return (
          <KuebikoQueryCell
            queryTemplate={queryTemplate}
            columnMappings={columnMappings}
            tooltip="Kuebiko Query"
          />
        );
      },
    },
    {
      field: "drill_down",
      headerName: "View Places",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const api = "/brand_places/get_brand_places_by_omni_biz_category";
        const apiParams = {
          omni_key: omni_key,
          country_code: country_code,
          business_category_id: params.row.actual_category,
        };

        return (
          <>
            <PlacesDrillDownModal api={api} apiParams={apiParams} />
          </>
        );
      },
    },
  ];

  const { data, isLoading: loading } = useQuery<any[]>({
    queryKey: ["business_categories_by_country", omni_key, country_code],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({
          omni_key: omni_key,
          country_code: country_code,
        });

        const response = await apiRequest<any[]>(
          `/brand_metadata/places_bizcategory_review_by_country?${params.toString()}`,
          { method: "GET" }
        );

        if (response.data) {
          return response.data;
        } else {
          throw new Error("No data found");
        }
      } catch (error) {
        if (error instanceof Error) {
          showError(
            `Failed to fetch places business categories to review for omni ${omni_key} + ${country_code}.`
          );
        } else {
          showError(
            "Failed to fetch business categories to review for omni: Unknown error"
          );
        }
        throw new Error("No data found");
      }
    },
    enabled: !!omni_key && !!country_code,
  });

  return (
    <Box className="metadata-review-container">
      <Box display="flex" alignItems="center" id="business-categories">
        <EasyAnchorLink id="business-categories" />
        <Typography variant="h6" gutterBottom sx={{ fontSize: "1.25rem" }}>
          Business Categories
        </Typography>
      </Box>

      <GaneshDataGrid
        gridId="placesReviewOmniBizCategories"
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={data && data.length > 0 ? true : false}
        customHeight={(data?.length ?? 0) > 0 ? "750px" : "300px"}
      />
    </Box>
  );
};

export default ReviewPlacesOmniBizCategories;
